import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import CustomCatalogPage from './components/catalog/CustomCatalogIndex';

import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import {
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { Root } from './components/Root';
import { useEntityList } from '@backstage/plugin-catalog-react';
import {
  AlertDisplay,
  OAuthRequestDialog,
  OverflowTooltip,
  TableColumn,
} from '@backstage/core-components';
import {SignInPage} from './components/SignInPage/SignInPage';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { DynamicColumnName } from './utils';
import { Entity } from '@backstage/catalog-model';
import {
  EntityRefLink,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';
import { CustomHomePage } from './components/home/CustomHomePage';
import {CapabilityTable} from "./components/catalog/CapabilityTable";
import { EntityNodeRenderer } from './components/catalog/EntityNodeRenderer';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { UnifiedThemeProvider } from '@backstage/theme';
import { catTheme } from './utils/catTheme';
import EIRTable from './components/catalog/EIRTable';
import ReleaseNotes from './components/Settings/ReleaseNotes';
import CustomSettingsPage from './components/Settings/CustomSettingsPage';
import OnboardingPage from './components/OnboardingPage/OnboardingPage';
import { ChatBotUI } from './components/chatBot/Chatbot'
import TrackAdoptionRequestPage from './components/TrackAdoptionRequest/TrackAdoptionRequestPage';

const app = createApp({
  apis,
  // Registered as the only app theme (variant 'light') so Backstage sets
  // data-theme-mode='light'. Without this, BUI components default to dark.
  themes: [
    {
      id: 'cat-theme',
      title: 'Service Catalog',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={catTheme} children={children} />
      ),
    },
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => <SignInPage {...props} />,
  },
});

const createNameColumn = (options?: {
  defaultKind?: string;
  customTitle?: string;
  highlight?: boolean;
}): TableColumn<CatalogTableRow> => {
  function formatContent(entity: Entity): string {
    return (
      entity.metadata?.title ||
      humanizeEntityRef(entity, {
        defaultKind: options?.defaultKind,
      })
    );
  }

  return {
    title: options?.customTitle || 'Name',
    field: 'resolved.entityRef',
    highlight: options?.highlight === undefined ? true : options.highlight,
    searchable: true,
    customSort({ entity: entity1 }, { entity: entity2 }) {
      return formatContent(entity1).localeCompare(formatContent(entity2));
    },
    render: ({ entity }) => (
      <EntityRefLink
        entityRef={entity}
        defaultKind={options?.defaultKind || 'Component'}
      />
    ),
  };
};

const createDynamicColumn = (
  title: string,
  property: string,
): TableColumn<CatalogTableRow> => {
  return {
    title: title,
    field: `entity.metadata.annotations.${property}`,
    searchable: true,
    customSort: (row1, row2) => {
      const val1 = row1?.entity?.metadata?.annotations?.[property];
      const val2 = row2?.entity?.metadata?.annotations?.[property];

      const title1 =
        typeof val1 === 'string' ? val1.trim().toLocaleLowerCase() : '';
      const title2 =
        typeof val2 === 'string' ? val2.trim().toLocaleLowerCase() : '';

      return title1.localeCompare(title2);
    },
    render: ({ entity }) => (
      <OverflowTooltip
        text={entity?.metadata?.annotations?.[property] ?? ''}
        placement="bottom-start"
      />
    ),
    width: 'auto',
  };
};

const createContractTestingColumn = (
  title: string,
  property: string,
): TableColumn<CatalogTableRow> => {
  return {
    title: title,
    render: ({ entity }) => {
      const contractTesting = entity?.spec?.contractTesting as any;
      const value = contractTesting?.[property] ?? '';
      
      // Handle arrays (like listOfApplications) - convert to comma-separated string
      let displayText = '';
      if (Array.isArray(value)) {
        displayText = value.join(', ');
      } else {
        displayText = value ?? '';
      }
      
      return (
        <OverflowTooltip
          text={displayText}
          placement="bottom-start"
        />
      );
    },
    width: 'auto',
  };
};

const createEIRColumn = (
  title: string,
  ruleName: string,
  field: 'compliant' | 'comments',
): TableColumn<CatalogTableRow> => {
  return {
    title: title,
    render: ({ entity }) => {
      const eir = entity?.spec?.entitlementIntegrationRules as any;
      const value = eir?.[ruleName]?.[field] ?? '';
      return (
        <OverflowTooltip
          text={String(value)}
          placement="bottom-start"
        />
      );
    },
    width: 'auto',
  };
};

// const heliosConsumerColumns = [createNameColumn({ defaultKind: 'API' })];

const openapiColumns = [
  createNameColumn({ defaultKind: 'API' }),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  createDynamicColumn(
    DynamicColumnName.TEAM,
    'backstage.io/team',
  ),
  createDynamicColumn(
    DynamicColumnName.SERVICE_OWNER,
    'backstage.io/serviceOwner',
  ),
  createDynamicColumn(
    DynamicColumnName.PRODUCT_OWNER,
    'backstage.io/productOwner',
  ),
  createDynamicColumn(DynamicColumnName.TECH_LEAD, 'backstage.io/techLead'),
  createDynamicColumn(
    DynamicColumnName.API_OWNERSHIP,
    'backstage.io/apiOwnership',
  ),
  createDynamicColumn(
    DynamicColumnName.SERVICE_SCOPE,
    'backstage.io/serviceScope',
  ),
  createDynamicColumn(
    DynamicColumnName.SERVICE_CATEGORY,
    'backstage.io/serviceCategory',
  ),
  createDynamicColumn(
    DynamicColumnName.SERVICE_NOW_ASSIGNMENT_GROUP,
    'backstage.io/serviceNowAssignmentGroup',
  ),
  createDynamicColumn(DynamicColumnName.DMP_DOMAIN, 'backstage.io/dmpDomain'),
  createDynamicColumn(
    DynamicColumnName.LEANIX_SERVICE,
    'backstage.io/trouxService',
  ),
  createDynamicColumn(
    DynamicColumnName.APIGEE_PROXY,
    'backstage.io/apigeeProxy',
  ),
  // CatalogTable.columns.createSystemColumn(),
  createDynamicColumn(
    DynamicColumnName.LAST_UPDATED,
    'backstage.io/lastReviewDate',
  ),
];

const serviceCapabilitiesColumns = [
  createNameColumn({ defaultKind: 'API' }),
  createDynamicColumn(
    DynamicColumnName.SERVICE_OWNER,
    'backstage.io/serviceOwner',
  ),
  createDynamicColumn(
    DynamicColumnName.ENFORCES_ENTITLEMENTS,
    'backstage.io/enforcesEntitlements',
  ),
  createDynamicColumn(
    DynamicColumnName.ENTITLEMENTS_CAT_CORP_ENABLED,
    'backstage.io/entitlementsCatCorpSupport',
  ),
  createDynamicColumn(
    DynamicColumnName.CUSTOMER_MERGE_FUNCTIONALITY,
    'backstage.io/customerMergeFunctionality',
  ),
  createDynamicColumn(
    DynamicColumnName.LOCALIZATION_TRANSLATIONS,
    'backstage.io/localization',
  ),
  createDynamicColumn(
    DynamicColumnName.SERVICE_TO_SERVICE_COMM_SUPPORT,
    'backstage.io/serviceToServiceCommunicationSupport',
  ),
  createDynamicColumn(
    DynamicColumnName.FORCED_FILTERS_SUPPORT,
    'backstage.io/forcedFilterSupport',
  ),
  createDynamicColumn(
    DynamicColumnName.INTERNAL_ACCESS_LOGGING,
    'backstage.io/internalAccessLogging',
  ),
  createDynamicColumn(
    DynamicColumnName.SUPPORT_AUDIT_LOGGING,
    'backstage.io/catSupportUserActionAuditLogging',
  ),
  createDynamicColumn(
    DynamicColumnName.ASSET_CENTRIC,
    'backstage.io/assetCentric',
  ),
  createDynamicColumn(
    DynamicColumnName.UNAUTHENTICATED_ACCESS,
    'backstage.io/unauthenticatedAccess',
  ),
  createContractTestingColumn(
    DynamicColumnName.CONTRACT_TESTING_IS_ENABLED,
    'isEnabled',
  ),
  createContractTestingColumn(
    DynamicColumnName.CONTRACT_TESTING_LIST_OF_APPLICATIONS,
    'listOfApplications',
  ),  
  createContractTestingColumn(
    DynamicColumnName.CONTRACT_TESTING_REASON,
    'reason',
  ),
  createDynamicColumn(
    DynamicColumnName.LAST_UPDATED,
    'backstage.io/lastReviewDate',
  ),
];

const EIRColumns=[
  createNameColumn({ defaultKind: 'API' }),
  createDynamicColumn(
    DynamicColumnName.SERVICE_OWNER,
    'backstage.io/serviceOwner',
  ),
  // assetConditionEnforcement
  createEIRColumn(DynamicColumnName.EIR_ASSET_CONDITION_ENFORCEMENT_COMPLIANT, 'assetConditionEnforcement', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_ASSET_CONDITION_ENFORCEMENT_COMMENTS, 'assetConditionEnforcement', 'comments'),
  // clientTypeAgnosticCompliance
  createEIRColumn(DynamicColumnName.EIR_CLIENT_TYPE_AGNOSTIC_COMPLIANCE_COMPLIANT, 'clientTypeAgnosticCompliance', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_CLIENT_TYPE_AGNOSTIC_COMPLIANCE_COMMENTS, 'clientTypeAgnosticCompliance', 'comments'),
  // commonLibraryStandardization
  createEIRColumn(DynamicColumnName.EIR_COMMON_LIBRARY_STANDARDIZATION_COMPLIANT, 'commonLibraryStandardization', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_COMMON_LIBRARY_STANDARDIZATION_COMMENTS, 'commonLibraryStandardization', 'comments'),
  // dynamicRuleExternalization
  createEIRColumn(DynamicColumnName.EIR_DYNAMIC_RULE_EXTERNALIZATION_COMPLIANT, 'dynamicRuleExternalization', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_DYNAMIC_RULE_EXTERNALIZATION_COMMENTS, 'dynamicRuleExternalization', 'comments'),
  // corpExperienceFlagResilience
  createEIRColumn(DynamicColumnName.EIR_CORP_EXPERIENCE_FLAG_RESILIENCE_COMPLIANT, 'corpExperienceFlagResilience', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_CORP_EXPERIENCE_FLAG_RESILIENCE_COMMENTS, 'corpExperienceFlagResilience', 'comments'),
  // entitlementsContractCompliance
  createEIRColumn(DynamicColumnName.EIR_ENTITLEMENTS_CONTRACT_COMPLIANCE_COMPLIANT, 'entitlementsContractCompliance', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_ENTITLEMENTS_CONTRACT_COMPLIANCE_COMMENTS, 'entitlementsContractCompliance', 'comments'),
  // filterConditionEnforcement
  createEIRColumn(DynamicColumnName.EIR_FILTER_CONDITION_ENFORCEMENT_COMPLIANT, 'filterConditionEnforcement', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_FILTER_CONDITION_ENFORCEMENT_COMMENTS, 'filterConditionEnforcement', 'comments'),
  // newAttributeTolerance
  createEIRColumn(DynamicColumnName.EIR_NEW_ATTRIBUTE_TOLERANCE_COMPLIANT, 'newAttributeTolerance', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_NEW_ATTRIBUTE_TOLERANCE_COMMENTS, 'newAttributeTolerance', 'comments'),
  // sessionCacheKeyStandard
  createEIRColumn(DynamicColumnName.EIR_SESSION_CACHE_KEY_STANDARD_COMPLIANT, 'sessionCacheKeyStandard', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_SESSION_CACHE_KEY_STANDARD_COMMENTS, 'sessionCacheKeyStandard', 'comments'),
  // standardInternalAccessControlImplementation
  createEIRColumn(DynamicColumnName.EIR_STANDARD_INTERNAL_ACCESS_CONTROL_COMPLIANT, 'standardInternalAccessControlImplementation', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_STANDARD_INTERNAL_ACCESS_CONTROL_COMMENTS, 'standardInternalAccessControlImplementation', 'comments'),
  // tokenDecodingRestriction
  createEIRColumn(DynamicColumnName.EIR_TOKEN_DECODING_RESTRICTION_COMPLIANT, 'tokenDecodingRestriction', 'compliant'),
  createEIRColumn(DynamicColumnName.EIR_TOKEN_DECODING_RESTRICTION_COMMENTS, 'tokenDecodingRestriction', 'comments'),
  createDynamicColumn(
    DynamicColumnName.LAST_UPDATED,
    'backstage.io/lastReviewDate',
  ),
]


const consumerDetailsColumns = [
  createNameColumn({ customTitle: 'Consumer Name', highlight: false }),
  createDynamicColumn(
    DynamicColumnName.PART_OF_CAT_DIGITAL,
    'backstage.io/partOfCatDigital',
  ),
  createDynamicColumn(DynamicColumnName.INDUSTRY, 'backstage.io/industry'),
  createDynamicColumn(
    DynamicColumnName.BUSINESS_UNIT,
    'backstage.io/businessUnit',
  ),
  createDynamicColumn(
    DynamicColumnName.BUSINESS_OWNER,
    'backstage.io/businessOwner',
  ),
  createDynamicColumn(
    DynamicColumnName.TECHNICAL_OWNER,
    'backstage.io/technicalOwner',
  ),
];

const consumersOnboardingTimelineExplorerColumns = [
  createDynamicColumn(
    DynamicColumnName.EXTERNAL_CONSUMERS,
    'backstage.io/externalConsumers',
  ),
  createNameColumn({ customTitle: 'Services/API', highlight: false }),
  createDynamicColumn(
    DynamicColumnName.ONBOARDING_DATE,
    'backstage.io/onboardingDate',
  ),
];

const componentColumns = [
  CatalogTable.columns.createNameColumn({ defaultKind: 'API' }),
  CatalogTable.columns.createSystemColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  createDynamicColumn(
    DynamicColumnName.PART_OF_CAT_DIGITAL,
    'backstage.io/partOfCatDigital',
  ),
  createDynamicColumn(DynamicColumnName.INDUSTRY, 'backstage.io/industry'),
  createDynamicColumn(
    DynamicColumnName.BUSINESS_UNIT,
    'backstage.io/businessUnit',
  ),
  createDynamicColumn(
    DynamicColumnName.BUSINESS_OWNER,
    'backstage.io/businessOwner',
  ),
  createDynamicColumn(
    DynamicColumnName.TECHNICAL_OWNER,
    'backstage.io/technicalOwner',
  ),
  createDynamicColumn(
    DynamicColumnName.LAST_UPDATED,
    'backstage.io/lastUpdated',
  ),
  createDynamicColumn(
    DynamicColumnName.ONBOARDING_DATE,
    'backstage.io/onboardingDate',
  ),
];

const resourceColumns = [
  CatalogTable.columns.createNameColumn({ defaultKind: 'API' }),
  CatalogTable.columns.createSystemColumn(),
  CatalogTable.columns.createOwnerColumn(),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createSpecLifecycleColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  createDynamicColumn(
    DynamicColumnName.LAST_UPDATED,
    'backstage.io/lastUpdated',
  ),
  createDynamicColumn(
    DynamicColumnName.ONBOARDING_DATE,
    'backstage.io/onboardingDate',
  ),
];

const customColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  console.log('DEBUG: entityListContext', entityListContext); // Log entire context

  entityListContext = useEntityList();

  // Ensure `filters` exist before accessing `kind`
  if (!entityListContext || !entityListContext.filters) {
    console.warn(
      'WARNING: entityListContext or filters are undefined! Returning default columns.',
    );
    return CatalogTable.defaultColumnsFunc(entityListContext);
  }

  const kindFilter =
    entityListContext.filters.kind?.value?.toLowerCase() ?? 'unknown'; // Default to 'unknown'

  if (kindFilter === 'api') {
    return [...openapiColumns];
  }

  if (kindFilter === 'component') {
    return [...componentColumns];
  }

  if (kindFilter === 'resource') {
    return [...resourceColumns];
  }

  console.log('WARNING: No matching kind, returning default columns.');

  console.warn('WARNING: No matching kind, returning default columns.');
  return CatalogTable.defaultColumnsFunc(entityListContext);
};

const serviceCapabilitiesColumnsFunc: CatalogTableColumnsFunc = () => {
  return [...serviceCapabilitiesColumns];
};

const EIRColumnsFunc: CatalogTableColumnsFunc = () => {
  return [...EIRColumns];
}

// @ts-ignore
const routes = (
  <FlatRoutes>
    {/* <Route path="/" element={<HomepageCompositionRoot />}>
      <CustomHomePage />
    </Route> */}
    <Route
      path="/"
      // element={<Navigate to="/vega?filters%5Bkind%5D=api&limit=500" />}
      // element={<Navigate to ="catalog?limit=500&filters%5Bkind%5D=API"/>}
      element={<Navigate to="/powerbi" />}
    />
    <CustomHomePage />

    <Route path="/catalog" element={<CatalogIndexPage />}>
      <CustomCatalogPage columns={customColumnsFunc} initialKind="API" />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route
      path="/capabilities"
      element={
        <CapabilityTable
          columns={serviceCapabilitiesColumnsFunc} 
          initialKind="API"
        />
      }
    />

    <Route
      path="/eir-matrix"
      element={
        <EIRTable
          columns={EIRColumnsFunc} 
          initialKind="API"
        />
      }
    />
    <Route path="/settings" element={<CustomSettingsPage />} />
    <Route path="/release-notes" element={<ReleaseNotes />} />
    {/* Route for onboarding page */}
    <Route path='/onboarding' element={<OnboardingPage/>} />
    <Route path='/track-requests' element={<TrackAdoptionRequestPage/>} />

    {/* <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} /> */}
    <Route
      path="/api-docs"
      element={<ApiExplorerPage columns={[...openapiColumns]} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    {/* <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route> */}

    {/* <Route
      path="/consumer-details"
      element={
        <ConsumerDetailsPage
          columns={[...consumerDetailsColumns]}
          intiallySelectedKind={'Component'}
          intiallyAllowedKind={['Component']}
          headerDisplayable={false}
          title={'Consumer Details'}
          pageTitleOverride={'Consumer Details'}
          hasDetails={false}
        />
      }
    /> */}

    <Route
      path="/catalog-graph"
      element={
        <div className="route-catalog-graph">
          <CatalogGraphPage renderNode={EntityNodeRenderer} />
        </div>}
    />

    <Route path="/chatbot" element={<ChatBotUI embedded={true} />} />
  </FlatRoutes>
);

export default app.createRoot(
  <Provider store={store}>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </Provider>
);
